/**
 * Is this microphone hearing anything?
 *
 * The one question a list of device names cannot answer, and the reason the
 * whole panel is worth having: an operator whose Chrome quietly switched to the
 * laptop's built-in microphone can pick the headset from a dropdown and still
 * have no idea whether they picked the right one until somebody on a call tells
 * them they cannot be heard.
 *
 * TWO SOURCES, and which one is used depends on whether a call is up.
 *
 * During a call the level comes from Twilio's own `inputVolume`, which reads the
 * stream the call is ACTUALLY sending. That is both cheaper and more honest than
 * a second reading: opening a parallel capture of the same microphone can make
 * the device renegotiate its sample rate, and doing that to a live conversation
 * to draw a bar is a bad trade.
 *
 * With no call up there is no such stream, so this opens one of its own for as
 * long as the panel is on screen and lets it go the moment it closes. That
 * capture is also what fetches the microphone permission - and with it every
 * device NAME in the lists next door, which the browser withholds until it is
 * granted.
 *
 * THE LEVEL NEVER PASSES THROUGH REACT. It is written straight onto the bar's
 * own node, sixty times a second, from inside the frame loop. Held in state it
 * re-rendered the whole picker on every frame instead - three sections, three
 * device lists and a fresh `Set` each time - and since both places this panel
 * lives are dropdowns that mount it the instant they open, that storm landed on
 * top of the panel's own 220ms entrance animation. The menu stuttered its way
 * open, every time, and it looked like something was still loading.
 */
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import type { CallAudio } from "./apply";

export type InputLevelState =
  /** Nothing is listening: the panel is closed, or the browser has no capture. */
  | "off"
  /** Waiting for the browser, which may be waiting for the operator. */
  | "asking"
  /** A level is being read. */
  | "live"
  /** The microphone was refused. Site settings are the only way back. */
  | "denied";

export interface InputLevel {
  /**
   * Goes on the bar itself. The hook writes a `scaleX` transform onto this node
   * per frame and nothing else, so a moving meter costs one composited layer
   * rather than a React render - see the note at the top of the file.
   */
  barRef: MutableRefObject<HTMLElement | null>;
  state: InputLevelState;
}

/** Fast up, slow down - a meter that fell as fast as speech does reads as noise. */
const RISE = 0.5;
const FALL = 0.12;

/** Same window the call meter uses: a phone leg is quiet by studio standards. */
const MIN_DB = -78;
const MAX_DB = -22;

export function useInputLevel(opts: {
  /** The panel is open. Nothing is captured, and no permission asked, until it is. */
  active: boolean;
  /** The chosen microphone, or "" for the system's. */
  deviceId: string;
  /** Twilio's helper while a call is up, and null the rest of the time. */
  audio: CallAudio | null;
}): InputLevel {
  const { active, deviceId, audio } = opts;
  const [state, setState] = useState<InputLevelState>("off");
  const barRef = useRef<HTMLElement | null>(null);
  // Read inside the frame loop rather than closed over, so a re-render cannot
  // tear the audio graph down and build it again mid-reading.
  const levelRef = useRef(0);

  /**
   * One reading, smoothed and painted.
   *
   * Kept in a ref rather than rebuilt per effect run so both sources share the
   * exact same smoothing and the same single line of DOM. A missing bar (the
   * panel has closed, or the browser enumerates nothing and the picker drew a
   * note instead) is not a failure - there is simply nowhere to paint.
   */
  const write = useRef((raw: number) => {
    const target = Math.min(1, Math.max(0, raw));
    const next = levelRef.current + (target - levelRef.current) * (target > levelRef.current ? RISE : FALL);
    levelRef.current = next;
    const bar = barRef.current;
    if (bar) bar.style.transform = `scaleX(${next.toFixed(3)})`;
  }).current;

  // ── The call's own stream, when there is a call ──────────────────────────
  useEffect(() => {
    if (!active || !audio) return;
    setState("live");
    audio.on("inputVolume", write);
    return () => {
      audio.removeListener("inputVolume", write);
      levelRef.current = 0;
      if (barRef.current) barRef.current.style.transform = "scaleX(0)";
      setState("off");
    };
  }, [active, audio, write]);

  // ── A capture of our own, when there is not ──────────────────────────────
  useEffect(() => {
    if (!active || audio) return;
    if (typeof navigator === "undefined" || typeof navigator.mediaDevices?.getUserMedia !== "function") {
      setState("off");
      return;
    }
    if (typeof window === "undefined" || typeof window.AudioContext !== "function") {
      setState("off");
      return;
    }

    let cancelled = false;
    let frame = 0;
    let ctx: AudioContext | null = null;
    let stream: MediaStream | null = null;
    setState("asking");

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // A named device rather than a constraint on the default, so the bar
          // is reading the microphone the operator is pointing at rather than
          // the one Chrome would have chosen for them.
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
      } catch {
        if (!cancelled) setState("denied");
        return;
      }
      if (cancelled) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }

      try {
        ctx = new AudioContext();
        if (ctx.state === "suspended") await ctx.resume();
      } catch {
        if (!cancelled) setState("off");
        return;
      }
      if (cancelled) return;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.minDecibels = MIN_DB;
      analyser.maxDecibels = MAX_DB;
      analyser.smoothingTimeConstant = 0.3;
      ctx.createMediaStreamSource(stream).connect(analyser);
      // NOT connected to the destination. The call meter has to reach one to be
      // pulled, but an analyser is driven by `getByteFrequencyData` alone - and
      // routing a live microphone to the speakers is how a room starts howling.

      const bins = new Uint8Array(analyser.frequencyBinCount);
      setState("live");

      const draw = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(bins);
        let sum = 0;
        for (const bin of bins) sum += bin;
        write(bins.length > 0 ? sum / bins.length / 255 : 0);
        frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);
    })();

    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      if (stream) for (const track of stream.getTracks()) track.stop();
      // Closed rather than suspended: a context left open holds an input device
      // for the rest of the session, and the tab keeps its recording dot lit.
      void ctx?.close().catch(() => {});
      levelRef.current = 0;
      if (barRef.current) barRef.current.style.transform = "scaleX(0)";
      setState("off");
    };
  }, [active, audio, deviceId, write]);

  return { barRef, state };
}
